import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
// Removed Layout import if this component is rendered within a Layout via <Outlet />
import APICall from "../../Utility/APICall";
import { paths } from "../../Utility/Constants";
import { useAuth } from "../../Utility/AuthProvider";
import { useUser } from "../../Utility/UserProvider"; // Assuming this provides user info
// import { useTenants } from "../Tenants/TenantContext";
import { useTenantContext } from "../Tenants/TenantContext";
import { useBuildings } from "../../Contexts/BuildingContext";
// import { useUnitDetails } from "./UnitDetailsContext";
import { useUnits } from "../../Contexts/UnitContext";
import Lottie from "lottie-react";
import loaderAnimation from "../../assets/animations/loader.json";

// Using react-icons consistently
import { FaBuilding, FaHome, FaUsers, FaPlusCircle } from "react-icons/fa"; // For cards and add buttons
import { HiOutlineBuildingStorefront } from "react-icons/hi2"; // Placeholder for images

import CustomImage from "../../Utility/CustomImage";
import VacantBtn from "../../components/Buttons/VacantBtn"; // Ensure these are styled with Tailwind
import RentedBtn from "../../components/Buttons/RentedBtn"; // Ensure these are styled with Tailwind

// Removed unused imports like Buildings, Units components if not directly rendered here in this view

// Stat Card Component (for top section)
const StatCard = ({
  title,
  count,
  icon,
  bgColorClass,
  textColorClass,
  linkTo,
}) => {
  const IconComponent = icon;
  return (
    <Link
      to={linkTo}
      className="flex-1 min-w-[200px] md:min-w-[240px] block text-decoration-none"
    >
      <div
        className={`p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out h-full flex flex-col justify-between ${bgColorClass}`}
      >
        <div className="flex justify-between items-start">
          <div>
            <p
              className={`text-lg font-semibold ${textColorClass || "text-gray-700"}`}
            >
              {title}
            </p>
            <p
              className={`text-3xl font-bold ${textColorClass || "text-gray-800"}`}
            >
              {count}
            </p>
          </div>
          {IconComponent && (
            <IconComponent
              className={`w-10 h-10 ${textColorClass || "text-gray-500"} opacity-70`}
            />
          )}
        </div>
        {/* Can add a "View Details >" link at the bottom if desired */}
      </div>
    </Link>
  );
};

function MyProperties() {
  const { jwt, user, isLoadingSession } = useAuth();
  // const { user } = useUser(); // Get user from UserProvider
  const {
    allTenants,
    isLoadingTenants,
    tenantError,
    fetchAllTenants,
    fetchTenantById,
    tenantData,
  } = useTenantContext();
  const { allBuildings, isLoadingBuildings, buildingError } = useBuildings();
  // const { unitDetails, isLoadingUnits, unitError } = useUnitDetails();
  const { allUnits, isLoadingUnits, unitError } = useUnits();

  const navigate = useNavigate();

  // Combined loading state for MyProperties
  const isPageLoading =
    isLoadingTenants || isLoadingBuildings || isLoadingUnits;
  // Combined error state
  const pageError = tenantError || buildingError || unitError;

  // const [buildingData, setBuildingData] = useState([]); // Renamed from tenantData for clarity
  // const [unitData, setUnitData] = useState([]);
  // const [loading, setLoading] = useState(true); // Single loading state for initial data

  // useEffect(() => {
  //   const fetchData = async () => {
  //     if (!jwt) return; // Don't fetch if no JWT
  //     setLoading(true);
  //     try {
  //       // Fetch buildings and units in parallel for better performance
  //       const [buildingResponse, unitResponse] = await Promise.all([
  //         // APICall("GET", paths.building.fetch, undefined, undefined, jwt),
  //         APICall("GET", paths.unit.fetch, undefined, undefined, jwt),
  //       ]);

  //       setBuildingData(buildingResponse?.response || []);
  //       setUnitData(unitResponse?.response || []);
  //     } catch (error) {
  //       console.error("Error fetching My Properties data:", error);
  //       // Optionally set an error state to display to the user
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchData();
  // }, [jwt]);

  // Now, calculate counts directly from context data
  // FIX: Safely access .length by providing a default empty array if the data is undefined
  const totalUnitsCount = allUnits?.length || 0;
  const totalBuildingsCount = allBuildings?.length || 0;
  const totalTenantsCount = allTenants?.length || 0; // This was already correct, but good to apply the same pattern

  // Calculate total tenants - assuming each building object has 'no_of_tenants'
  // const totalTenantsCount = buildingData.reduce((total, building) => {
  //   return total + (parseInt(building.no_of_tenants) || 0);
  // }, 0);

  // const totalTenantsCount = allTenants.length;

  // ------ Render Helper for Building Item ------
  const renderBuildingItem = (building) => (
    <div
      key={building.entity_id || building.id} // Use a stable key
      className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-150 ease-in-out bg-white"
    >
      <div className="flex-shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-md bg-gray-100 flex items-center justify-center overflow-hidden">
        {building.media &&
        building.media.length > 0 &&
        building.media[0].location ? (
          <CustomImage
            location={building.media[0].location}
            jwt={jwt}
            className="w-full h-full object-cover"
            alt={`${building.building_name} image`}
          />
        ) : (
          <HiOutlineBuildingStorefront className="w-12 h-12 text-gray-400" />
        )}
      </div>
      <div className="flex-grow min-w-0">
        <p className="text-md font-semibold text-gray-800 truncate">
          {building.building_name}
        </p>
        <p className="text-sm text-gray-500 truncate mb-2">
          {building.address}
        </p>
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <FaHome /> {building.no_of_units || 0} Units
          </span>
          <span className="flex items-center gap-1">
            <FaUsers /> {building.no_of_tenants || 0} Tenants
          </span>
        </div>
      </div>
      <button
        onClick={() =>
          navigate(`/dashboard/add-new-building`, {
            state: { buildingToEdit: building },
          })
        }
        className="text-blue-600 hover:text-blue-800 text-xs font-medium p-2 rounded-md hover:bg-blue-50"
        aria-label={`Edit ${building.building_name}`}
      >
        Edit {/* Or an Edit Icon */}
      </button>
    </div>
  );

  // ------ Render Helper for Unit Item ------
  const renderUnitItem = (unit) => (
    <div
      key={unit.entity_id || unit.id} // Use a stable key
      className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-150 ease-in-out bg-white"
    >
      <div className="flex-shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-md bg-gray-100 flex items-center justify-center overflow-hidden">
        {unit.media && unit.media.length > 0 && unit.media[0].location ? (
          <CustomImage
            location={unit.media[0].location}
            jwt={jwt}
            className="w-full h-full object-cover"
            alt={`${unit.unit_name} image`}
          />
        ) : (
          <FaHome className="w-10 h-10 text-gray-400" /> // Different placeholder for unit
        )}
      </div>
      <div className="flex-grow min-w-0">
        <p className="text-md font-semibold text-gray-800 truncate">
          {unit.unit_name}
        </p>
        <p className="text-sm text-gray-500 truncate mb-2">
          {unit.building_name}
        </p>
        <div>
          {unit.occupancy_status === "vacant" ? (
            <VacantBtn>Vacant</VacantBtn>
          ) : (
            <RentedBtn>Rented</RentedBtn>
          )}
        </div>
      </div>
      {/* <button
        onClick={() =>
          navigate(`/MyProperties/${unit.unit_name}`, { state: unit })
        } 
        className="text-blue-600 hover:text-blue-800 text-xs font-medium p-2 rounded-md hover:bg-blue-50"
        aria-label={`Edit ${unit.unit_name}`}
      >
        Edit 
      </button> */}

      <button
        // http://localhost:3000/dashboard/my-properties/998250798868155199
        onClick={() =>
          navigate(`/dashboard/my-properties/${unit.entity_id}`, {
            state: { unit },
          })
        }
        className="text-blue-600 hover:text-blue-800 text-xs font-medium p-2 rounded-md hover:bg-blue-50"
        aria-label={`Edit ${unit.unit_name}`}
      >
        Edit {/* Or an Edit Icon */}
      </button>
    </div>
  );

  // Main Loading State for the entire page
  if (isPageLoading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-150px)]">
        {" "}
        {/* Adjust height as needed */}
        <Lottie
          animationData={loaderAnimation}
          loop={true}
          style={{ width: 150, height: 150 }}
        />
      </div>
    );
  }

  return (
    // Page container (assuming Layout.jsx provides the main page padding)
    <div className="space-y-8">
      {/* Welcome Message */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-1">
          Welcome, {user?.first_name || "User"}!
        </h1>
        <p className="text-sm text-gray-600">
          Here's an overview of your properties. You can manage your buildings,
          units, and tenants.
        </p>
      </div>

      {/* Stats Cards Section */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Total Buildings"
          count={totalBuildingsCount}
          icon={FaBuilding}
          bgColorClass="bg-gradient-to-br from-blue-400 to-blue-600"
          textColorClass="text-white"
          linkTo="/dashboard/buildings"
        />
        <StatCard
          title="Total Units"
          count={totalUnitsCount}
          icon={FaHome}
          bgColorClass="bg-gradient-to-br from-purple-400 to-purple-600"
          textColorClass="text-white"
          linkTo="/dashboard/units"
        />
        <StatCard
          title="Total Tenants"
          count={tenantData.length || totalTenantsCount}
          icon={FaUsers}
          bgColorClass="bg-gradient-to-br from-green-400 to-green-600"
          textColorClass="text-white"
          linkTo="/dashboard/tenants-list"
        />
      </section>

      {/* Buildings and Units Preview Sections - Side by Side */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Buildings Section */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-700">
              Recent Buildings
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/dashboard/add-new-building")}
                className="flex items-center gap-1.5 text-sm bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-md transition-colors"
                aria-label="Add New Building"
              >
                <FaPlusCircle /> Add
              </button>
              <Link
                to="/dashboard/buildings"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium py-2 px-3 rounded-md hover:bg-blue-50 transition-colors"
              >
                View All
              </Link>
            </div>
          </div>
          {allBuildings?.length > 0 ? (
            allBuildings.slice(0, 2).map(renderBuildingItem) // Show first 2 previews
          ) : (
            <p className="text-sm text-gray-500 py-4 text-center">
              No buildings added yet.
            </p>
          )}
        </div>

        {/* Units Section */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-700">
              Recent Units
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/dashboard/add-new-unit")}
                className="flex items-center gap-1.5 text-sm bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-md transition-colors"
                aria-label="Add New Unit"
              >
                <FaPlusCircle /> Add
              </button>
              <Link
                to="/dashboard/units"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium py-2 px-3 rounded-md hover:bg-blue-50 transition-colors"
              >
                View All
              </Link>
            </div>
          </div>
          {allUnits?.length > 0 ? (
            allUnits.slice(0, 2).map(renderUnitItem) // Show first 2 previews
          ) : (
            <p className="text-sm text-gray-500 py-4 text-center">
              No units added yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

export default MyProperties;
